import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import {
  freebordAccessories,
  freebordDecks,
  freebordSpotlightItems,
  freebordTrucksBindings,
  freebordWheels
} from '../data/freebord';
import FallbackImage from '../components/FallbackImage';

const FreebordPage = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language as 'en' | 'he';
  const [activeTab, setActiveTab] = useState<'boards' | 'trucks' | 'wheels' | 'decks' | 'accessories' | 'media'>('boards');
  const [selectedSizes, setSelectedSizes] = useState<Record<string, string>>(
    Object.fromEntries(freebordSpotlightItems.map((item) => [item.id, item.sizes[0]]))
  );
  const [selectedHoodieSizes, setSelectedHoodieSizes] = useState<Record<string, string>>(
    Object.fromEntries(
      freebordSpotlightItems
        .filter((item) => item.hoodieSizes?.length)
        .map((item) => [item.id, item.hoodieSizes![0]])
    )
  );

  const buildWhatsappLink = (itemId: string) => {
    const item = freebordSpotlightItems.find((entry) => entry.id === itemId);
    if (!item) return '#';

    const boardSize = selectedSizes[itemId];
    const hoodieSize = item.hoodieSizes ? selectedHoodieSizes[itemId] : undefined;
    const message =
      item.orderType === 'pack'
        ? `Hi, I want to order ${item.title.en} from BlueBoard Israel. Board size: ${boardSize}. Hoodie size: ${hoodieSize}.`
        : `Hi, I want to order ${item.title.en} from BlueBoard Israel. Board size: ${boardSize}.`;

    return `https://wa.me/972503901000?text=${encodeURIComponent(message)}`;
  };

  const buildAccessoryWhatsappLink = (label: string) =>
    `https://wa.me/972503901000?text=${encodeURIComponent(`Hi, I want to order ${label} from BlueBoard Israel.`)}`;

  const storeTabs = [
    { id: 'boards' as const, label: t('freebord.tabBoards') },
    { id: 'trucks' as const, label: t('freebord.tabTrucksBindings') },
    { id: 'wheels' as const, label: t('freebord.tabWheels') },
    { id: 'decks' as const, label: t('freebord.tabDecks') },
    { id: 'accessories' as const, label: t('freebord.tabAccessories') },
    { id: 'media' as const, label: t('freebord.tabMedia') }
  ];

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'BlueBoard x FREEBORD',
    url: 'https://blueboard.bloodshop.org/freebord',
    description: t('freebord.description')
  };

  return (
    <>
      <SEO
        title="FREEBORD | BlueBoard"
        description={t('freebord.description')}
        keywords="freebord, freebord 5x, freebord 5, street snowboard, blueboard israel"
        url="/freebord"
        lang={currentLang}
        structuredData={structuredData}
      />
      <div className="min-h-screen bg-neutral-950 text-white" dir={i18n.language === 'he' ? 'rtl' : 'ltr'}>
        <section className="relative overflow-hidden border-b border-white/10 bg-[radial-gradient(circle_at_top,_rgba(56,189,248,0.22),_transparent_42%),linear-gradient(135deg,_#020617_0%,_#111827_45%,_#172554_100%)]">
          <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="mb-6 inline-flex rounded-full border border-cyan-400/30 bg-cyan-400/10 px-4 py-2 text-sm font-semibold uppercase tracking-[0.2em] text-cyan-200">
                {t('freebord.badge')}
              </div>
              <h1 className="max-w-3xl text-5xl font-black uppercase tracking-tight sm:text-6xl">
                {t('freebord.title')}
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-200">
                {t('freebord.description')}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                {storeTabs.map(({ id, label }) => (
                  <button
                    key={id}
                    onClick={() => setActiveTab(id)}
                    className={`rounded-full px-5 py-3 text-sm font-bold uppercase tracking-[0.16em] transition ${
                      activeTab === id
                        ? 'bg-cyan-400 text-slate-950'
                        : 'border border-white/20 text-white hover:border-cyan-300 hover:text-cyan-200'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <button
                  onClick={() => setActiveTab('boards')}
                  className="rounded-full bg-cyan-400 px-6 py-3 font-bold text-slate-950 transition hover:bg-cyan-300"
                >
                  {t('freebord.ctaExplore')}
                </button>
                <a
                  href="https://wa.me/972503901000?text=Hi%2C%20I%20want%20to%20order%20a%20FREEBORD%20from%20BlueBoard%20Israel."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full border border-white/20 px-6 py-3 font-bold text-white transition hover:border-cyan-300 hover:text-cyan-200"
                >
                  {t('freebord.ctaWhatsapp')}
                </a>
              </div>
            </div>
          </div>
        </section>

        <div className="sticky top-16 z-20 border-b border-white/10 bg-neutral-950/90 backdrop-blur">
          <div className="mx-auto flex max-w-7xl gap-3 overflow-x-auto px-4 py-4 sm:px-6 lg:px-8">
            {storeTabs.map(({ id, label }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`rounded-full px-5 py-3 text-sm font-bold uppercase tracking-[0.16em] transition ${
                  activeTab === id
                    ? 'bg-cyan-400 text-slate-950'
                    : 'border border-white/15 text-slate-200 hover:border-cyan-300 hover:text-cyan-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {activeTab === 'boards' && (
        <section id="new-arrivals" className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="mb-10 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-cyan-300">
                {t('freebord.arrivalsEyebrow')}
              </p>
              <h2 className="mt-2 text-3xl font-black uppercase sm:text-4xl">
                {t('freebord.arrivalsTitle')}
              </h2>
            </div>
            <p className="max-w-2xl text-sm leading-7 text-slate-300">
              {t('freebord.arrivalsSubtitle')}
            </p>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            {freebordSpotlightItems.map((item) => (
              <article
                key={item.id}
                className={`rounded-3xl border border-white/10 bg-gradient-to-br ${item.theme} p-6 shadow-2xl shadow-black/30`}
              >
                <div className="flex aspect-square items-center justify-center rounded-2xl bg-white/95 p-5">
                  <FallbackImage
                    srcs={item.imageSources}
                    alt={item.title[currentLang]}
                    className="h-full w-full object-contain"
                    loading="lazy"
                  />
                </div>
                <div className="mt-6 flex items-start justify-between gap-4">
                  <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-white/90">
                    {item.accent}
                  </span>
                  <span className="text-sm font-semibold text-cyan-100">₪ {item.nisPrice.toLocaleString()}</span>
                </div>
                <h3 className="mt-8 text-3xl font-black uppercase">{item.title[currentLang]}</h3>
                <p className="mt-4 min-h-[96px] text-sm leading-7 text-white/85">
                  {item.description[currentLang]}
                </p>
                <p className="mt-3 text-sm font-semibold text-cyan-50">{item.tagline[currentLang]}</p>
                <ul className="mt-4 space-y-2 text-sm leading-7 text-white/85">
                  {item.bullets[currentLang].map((bullet) => (
                    <li key={bullet}>- {bullet}</li>
                  ))}
                </ul>
                <div className="mt-6 grid gap-4 md:grid-cols-2">
                  <label className="text-sm font-semibold text-cyan-50">
                    {t('freebord.boardSize')}
                    <select
                      value={selectedSizes[item.id]}
                      onChange={(event) =>
                        setSelectedSizes((current) => ({ ...current, [item.id]: event.target.value }))
                      }
                      className="mt-2 w-full rounded-xl border border-white/20 bg-black/30 px-4 py-3 text-white outline-none"
                    >
                      {item.sizes.map((size) => (
                        <option key={size} value={size} className="text-black">
                          {size}
                        </option>
                      ))}
                    </select>
                  </label>
                  {item.hoodieSizes && (
                    <label className="text-sm font-semibold text-cyan-50">
                      {t('freebord.hoodieSize')}
                      <select
                        value={selectedHoodieSizes[item.id]}
                        onChange={(event) =>
                          setSelectedHoodieSizes((current) => ({ ...current, [item.id]: event.target.value }))
                        }
                        className="mt-2 w-full rounded-xl border border-white/20 bg-black/30 px-4 py-3 text-white outline-none"
                      >
                        {item.hoodieSizes.map((size) => (
                          <option key={size} value={size} className="text-black">
                            {size}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                </div>
                <div className="mt-6 grid grid-cols-2 gap-3">
                  {item.galleryImages.slice(0, 4).map((imageUrl) => (
                    <div key={imageUrl} className="flex aspect-square items-center justify-center rounded-xl border border-white/10 bg-white/95 p-3">
                      <FallbackImage
                        srcs={[imageUrl]}
                        alt={item.title[currentLang]}
                        className="h-full w-full object-contain"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
                <a
                  href={buildWhatsappLink(item.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-8 inline-flex rounded-full bg-white px-5 py-3 text-sm font-bold uppercase tracking-[0.16em] text-slate-950 transition hover:bg-cyan-100"
                >
                  {t('freebord.orderWhatsapp')}
                </a>
              </article>
            ))}
          </div>
        </section>
        )}

        {activeTab === 'accessories' && (
        <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="mb-10 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-cyan-300">
                {t('freebord.accessoriesEyebrow')}
              </p>
              <h2 className="mt-2 text-3xl font-black uppercase sm:text-4xl">
                {t('freebord.accessoriesTitle')}
              </h2>
            </div>
            <p className="max-w-2xl text-sm leading-7 text-slate-300">
              {t('freebord.accessoriesSubtitle')}
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-5">
            {freebordAccessories.map((item) => (
              <article key={item.id} className="rounded-3xl border border-white/10 bg-white/5 p-5 shadow-xl shadow-black/20">
                <div className="flex aspect-square items-center justify-center rounded-2xl bg-white/95 p-4">
                  <FallbackImage
                    srcs={item.imageSources}
                    alt={item.title[currentLang]}
                    className="h-full w-full object-contain"
                    loading="lazy"
                  />
                </div>
                <h3 className="mt-5 text-xl font-black uppercase text-white">{item.title[currentLang]}</h3>
                <p className="mt-3 min-h-[96px] text-sm leading-7 text-slate-300">{item.description[currentLang]}</p>
                <div className="mt-4 text-lg font-bold text-cyan-100">₪ {item.nisPrice.toLocaleString()}</div>
                <a
                  href={buildAccessoryWhatsappLink(item.whatsappLabel)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-5 inline-flex rounded-full bg-white px-5 py-3 text-sm font-bold uppercase tracking-[0.16em] text-slate-950 transition hover:bg-cyan-100"
                >
                  {t('freebord.orderWhatsapp')}
                </a>
              </article>
            ))}
          </div>
        </section>
        )}

        {activeTab === 'wheels' && (
        <CategoryGrid
          title={t('freebord.wheelsTitle')}
          subtitle={t('freebord.wheelsSubtitle')}
          eyebrow={t('freebord.wheelsEyebrow')}
          items={freebordWheels}
          currentLang={currentLang}
          onOrder={buildAccessoryWhatsappLink}
          cta={t('freebord.orderWhatsapp')}
        />
        )}

        {activeTab === 'decks' && (
        <CategoryGrid
          title={t('freebord.decksTitle')}
          subtitle={t('freebord.decksSubtitle')}
          eyebrow={t('freebord.decksEyebrow')}
          items={freebordDecks}
          currentLang={currentLang}
          onOrder={buildAccessoryWhatsappLink}
          cta={t('freebord.orderWhatsapp')}
        />
        )}

        {activeTab === 'trucks' && (
        <CategoryGrid
          title={t('freebord.trucksBindingsTitle')}
          subtitle={t('freebord.trucksBindingsSubtitle')}
          eyebrow={t('freebord.trucksBindingsEyebrow')}
          items={freebordTrucksBindings}
          currentLang={currentLang}
          onOrder={buildAccessoryWhatsappLink}
          cta={t('freebord.orderWhatsapp')}
        />
        )}

        {activeTab === 'media' && (
        <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="mb-10">
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-cyan-300">
              {t('freebord.mediaEyebrow')}
            </p>
            <h2 className="mt-2 text-3xl font-black uppercase sm:text-4xl">
              {t('freebord.mediaTitle')}
            </h2>
            <p className="mt-4 max-w-2xl text-base leading-8 text-slate-300">
              {t('freebord.mediaSubtitle')}
            </p>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            {freebordSpotlightItems.filter((item) => item.video).map((item) => (
              <div key={item.id} className="overflow-hidden rounded-3xl border border-white/10 bg-black/30">
                <div className="aspect-video">
                  <iframe
                    src={item.video}
                    title={item.title[currentLang]}
                    className="h-full w-full"
                    loading="lazy"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                </div>
                <div className="p-5">
                  <div className="text-lg font-bold text-white">{item.title[currentLang]}</div>
                  <div className="mt-2 text-sm leading-7 text-slate-300">{item.description[currentLang]}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
        )}

        <section className="border-y border-white/10 bg-white/5">
          <div className="mx-auto grid max-w-7xl gap-8 px-4 py-20 sm:px-6 lg:grid-cols-[1.1fr_0.9fr] lg:px-8">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-cyan-300">
                {t('freebord.flowEyebrow')}
              </p>
              <h2 className="mt-2 text-3xl font-black uppercase sm:text-4xl">
              {t('freebord.flowTitle')}
              </h2>
              <p className="mt-6 max-w-2xl text-base leading-8 text-slate-300">
                {t('freebord.flowIntro')}
              </p>
            </div>
            <div className="space-y-4">
              {(['step1', 'step2', 'step3', 'step4'] as const).map((key, index) => (
                <div key={key} className="rounded-2xl border border-white/10 bg-black/30 p-5">
                  <div className="text-sm font-black uppercase tracking-[0.24em] text-cyan-300">
                    {String(index + 1).padStart(2, '0')}
                  </div>
                  <div className="mt-2 text-lg font-bold text-white">{t(`freebord.${key}Title`)}</div>
                  <p className="mt-2 text-sm leading-7 text-slate-300">{t(`freebord.${key}Body`)}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="rounded-3xl border border-cyan-400/20 bg-cyan-400/10 p-8">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-cyan-200">
              {t('freebord.pocNoteTitle')}
            </p>
            <p className="mt-4 max-w-4xl text-base leading-8 text-slate-100">
              {t('freebord.pocNoteBody')}
            </p>
            <p className="mt-4 text-sm leading-7 text-cyan-100/90">
              {t('freebord.whatsappNote')}
            </p>
          </div>
        </section>
      </div>
    </>
  );
};

type CategoryGridItem = {
  id: string;
  title: { en: string; he: string };
  description: { en: string; he: string };
  nisPrice: number;
  imageSources: string[];
  whatsappLabel: string;
};

const CategoryGrid = ({
  title,
  subtitle,
  eyebrow,
  items,
  currentLang,
  onOrder,
  cta
}: {
  title: string;
  subtitle: string;
  eyebrow: string;
  items: CategoryGridItem[];
  currentLang: 'en' | 'he';
  onOrder: (label: string) => string;
  cta: string;
}) => (
  <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
    <div className="mb-10 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
      <div>
        <p className="text-sm font-semibold uppercase tracking-[0.24em] text-cyan-300">{eyebrow}</p>
        <h2 className="mt-2 text-3xl font-black uppercase sm:text-4xl">{title}</h2>
      </div>
      <p className="max-w-2xl text-sm leading-7 text-slate-300">{subtitle}</p>
    </div>

    <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
      {items.map((item) => (
        <article key={item.id} className="rounded-3xl border border-white/10 bg-white/5 p-5 shadow-xl shadow-black/20">
          <div className="flex aspect-square items-center justify-center rounded-2xl bg-white/95 p-4">
            <FallbackImage
              srcs={item.imageSources}
              alt={item.title[currentLang]}
              className="h-full w-full object-contain"
              loading="lazy"
            />
          </div>
          <h3 className="mt-5 text-xl font-black uppercase text-white">{item.title[currentLang]}</h3>
          <p className="mt-3 min-h-[96px] text-sm leading-7 text-slate-300">{item.description[currentLang]}</p>
          <div className="mt-4 text-lg font-bold text-cyan-100">₪ {item.nisPrice.toLocaleString()}</div>
          <a
            href={onOrder(item.whatsappLabel)}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-5 inline-flex rounded-full bg-white px-5 py-3 text-sm font-bold uppercase tracking-[0.16em] text-slate-950 transition hover:bg-cyan-100"
          >
            {cta}
          </a>
        </article>
      ))}
    </div>
  </section>
);

export default FreebordPage;
